import { useState, useEffect, useRef } from "react";
import QRScanner from "../../components/QRScanner";
import WristbandScanner from "./WristbandScanner";
import { useNavigate, Link } from "react-router-dom";
import { ticketsApi } from "../../utils/api";

const TABS = ["Dashboard","My Events","Create Event","Attendees","QR Scanner","Wristbands","Payouts"];

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [search, setSearch] = useState("");
  const [scanInput, setScanInput] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [wristbandInput, setWristbandInput] = useState("");
  const [wristbandLookup, setWristbandLookup] = useState("");
  const [wristbandLookupResult, setWristbandLookupResult] = useState(null);
  const [wristbandLookupLoading, setWristbandLookupLoading] = useState(false);
  const [scannerMode, setScannerMode] = useState("ticket"); // ticket | wristband
  const [wristbandResult, setWristbandResult] = useState(null);
  const [wristbandLoading, setWristbandLoading] = useState(false);
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const orgName = localStorage.getItem("organiser_name") || "Organiser";
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const token = localStorage.getItem("organiser_token");

  useEffect(() => {
    if (!token) { navigate("/organiser-login"); return; }
    ticketsApi.getMyEvents()
      .then(d => { setEvents(d.events||[]); if(d.events?.length) setActiveEvent(d.events[0]); })
      .catch(() => navigate("/organiser-login"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeEvent) return;
    ticketsApi.getEventStats(activeEvent.id).then(d => setStats(d)).catch(()=>{});
  }, [activeEvent]);

  useEffect(() => {
    if (!activeEvent || tab !== 3) return;
    ticketsApi.getAttendees(activeEvent.id, search ? {search} : {}).then(d => setAttendees(d.attendees||[])).catch(()=>{});
  }, [activeEvent, tab, search]);

  useEffect(() => {
    if (!activeEvent || tab !== 4) return;
    ticketsApi.getCheckins(activeEvent.id).then(d => setCheckins(d.checkins||[])).catch(()=>{});
  }, [activeEvent, tab]);

  const lookupWristband = async () => {
    if (!wristbandLookup.trim()) return;
    setWristbandLookupLoading(true);
    setWristbandLookupResult(null);
    try {
      const d = await ticketsApi.request('GET', `/api/tickets/manage/wristband/lookup/${wristbandLookup.trim()}`);
      setWristbandLookupResult(d);
    } catch(e) { setWristbandLookupResult({ success:false, error:e.message }); }
    setWristbandLookupLoading(false);
  };

  const assignWristband = async () => {
    if (!wristbandInput.trim() || !scanResult?.ticket?.uid) return;
    setWristbandLoading(true);
    try {
      const d = await ticketsApi.request('POST', '/api/tickets/manage/wristband/assign', {
        ticket_uid: scanResult.ticket.uid,
        wristband_number: wristbandInput.trim(),
        event_id: activeEvent?.id,
      });
      setWristbandResult(d);
      if (d.success) setWristbandInput("");
    } catch(e) { setWristbandResult({ success:false, error:e.message }); }
    setWristbandLoading(false);
  };

  const doScan = async () => {
    if (!scanInput.trim() || !activeEvent) return;
    setWristbandInput(""); setWristbandResult(null);
    try {
      const r = await ticketsApi.processCheckin(scanInput.trim(), activeEvent.id);
      setScanResult(r);
      if (r.result === "success") ticketsApi.getCheckins(activeEvent.id).then(d => setCheckins(d.checkins||[]));
    } catch(e) { setScanResult({result:"invalid", message:e.message}); }
    setScanInput("");
  };

  const s = stats?.stats || {};
  const checkedIn = parseInt(s.checkedIn)||0;
  const total = parseInt(s.totalTickets)||0;
  const pct = total ? Math.round(checkedIn/total*100) : 0;
  const revenue = parseInt(s.totalRevenue)||0;

  const inpStyle = { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)" };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{background:"#050508"}}><div className="w-10 h-10 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>;

  return (
    <div className="min-h-screen" style={{background:"#050508"}}>
      {/* Top bar */}
      <div className="sticky top-0 z-40 px-6 py-3 flex items-center justify-between" style={{background:"rgba(5,5,8,0.95)",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm" style={{background:"linear-gradient(135deg,#9b3fe8,#f59e0b)"}}>N</div>
          <span className="text-white font-semibold text-sm">Event Manager</span>
        </div>
        <div className="flex items-center gap-3">
          {events.length > 1 && (
            <select onChange={e=>setActiveEvent(events.find(x=>x.id===parseInt(e.target.value)))}
              className="text-xs text-gray-300 rounded-lg px-3 py-1.5 border border-white/10"
              style={{background:"rgba(255,255,255,0.06)"}}>
              {events.map(e=><option key={e.id} value={e.id}>{e.title}</option>)}
            </select>
          )}
          <span className="text-xs text-gray-400">Hi, {orgName}</span>
          <button onClick={()=>{localStorage.removeItem("organiser_token");localStorage.removeItem("organiser_name");navigate("/organiser-login");}}
            className="text-xs text-red-400 hover:text-red-300 glass px-3 py-1.5 rounded-lg transition">Sign Out</button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-52 min-h-screen p-4 flex-shrink-0" style={{background:"rgba(0,0,0,0.3)",borderRight:"1px solid rgba(255,255,255,0.06)"}}>
          <div className="space-y-1">
            {TABS.map((t,i)=>(
              <button key={t} onClick={()=>setTab(i)}
                className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${tab===i?"bg-brand-700 text-white":"text-gray-400 hover:text-white hover:bg-white/5"}`}>
                {["📊","🎪","➕","👥","📷","💰"][i]} {t}
              </button>
            ))}
          </div>
          {activeEvent && (
            <div className="mt-6 p-3 rounded-xl border border-white/10" style={{background:"rgba(155,63,232,0.1)"}}>
              <p className="text-xs text-gray-500 mb-1">Active Event</p>
              <p className="text-white text-xs font-medium line-clamp-2">{activeEvent.title}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${activeEvent.status==="on_sale"?"bg-green-900/50 text-green-300":"bg-gray-800 text-gray-400"}`}>{activeEvent.status}</span>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 p-6 overflow-auto">

          {/* ── DASHBOARD ── */}
          {tab===0 && (
            <div>
              <h1 className="font-display text-2xl font-bold text-white mb-6">Dashboard</h1>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  {l:"Total Tickets", v:total, c:"#9b3fe8"},
                  {l:"Checked In",    v:checkedIn, c:"#1D9E75"},
                  {l:"Revenue",       v:`LKR ${(revenue/1000).toFixed(0)}K`, c:"#f59e0b"},
                  {l:"Events",        v:events.length, c:"#378ADD"},
                ].map(x=>(
                  <div key={x.l} className="glass rounded-2xl p-5">
                    <p className="text-xs text-gray-500 mb-1">{x.l}</p>
                    <p className="text-2xl font-bold text-white">{x.v}</p>
                    <div className="h-0.5 rounded-full mt-3 opacity-60" style={{background:x.c}}/>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <div className="glass rounded-2xl p-5">
                  <h2 className="font-semibold text-white mb-4">Ticket Tiers</h2>
                  <div className="space-y-4">
                    {(stats?.tiers||[]).map(t=>{
                      const p=Math.round((t.sold_count/t.capacity)*100);
                      const colors={blue:"#378ADD",gold:"#f59e0b",purple:"#9b3fe8",teal:"#1D9E75",red:"#ef4444"};
                      return (
                        <div key={t.id}>
                          <div className="flex justify-between text-sm mb-1">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{background:colors[t.color]||"#9b3fe8"}}/>
                              <span className="text-white">{t.name}</span>
                            </div>
                            <span className="text-gray-400">{t.sold_count}/{t.capacity}</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full">
                            <div className="h-full rounded-full transition-all" style={{width:`${p}%`,background:colors[t.color]||"#9b3fe8"}}/>
                          </div>
                          <p className="text-xs text-gray-600 mt-0.5">LKR {parseInt(t.price).toLocaleString()} · {p}% sold</p>
                        </div>
                      );
                    })}
                    {!stats?.tiers?.length && <p className="text-gray-500 text-sm text-center py-4">No tiers data</p>}
                  </div>
                </div>

                <div className="glass rounded-2xl p-5">
                  <h2 className="font-semibold text-white mb-4">Check-in Progress</h2>
                  <div className="flex items-center justify-center mb-4">
                    <div className="relative w-32 h-32">
                      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                        <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="12"/>
                        <circle cx="60" cy="60" r="50" fill="none" stroke="#9b3fe8" strokeWidth="12"
                          strokeDasharray={`${2*Math.PI*50}`}
                          strokeDashoffset={`${2*Math.PI*50*(1-pct/100)}`}
                          strokeLinecap="round" className="transition-all duration-500"/>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <p className="text-3xl font-bold text-white">{pct}%</p>
                        <p className="text-xs text-gray-400">arrived</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-green-900/20 rounded-xl border border-green-800/30">
                      <p className="text-2xl font-bold text-green-400">{checkedIn}</p>
                      <p className="text-xs text-gray-400">Checked In</p>
                    </div>
                    <div className="text-center p-3 glass rounded-xl">
                      <p className="text-2xl font-bold text-white">{total-checkedIn}</p>
                      <p className="text-xs text-gray-400">Remaining</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── MY EVENTS ── */}
          {tab===1 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-display text-2xl font-bold text-white">My Events</h1>
                <button onClick={()=>setTab(2)} className="text-sm bg-brand-700 hover:bg-brand-600 text-white px-4 py-2 rounded-xl transition">+ Create Event</button>
              </div>
              {events.length === 0 ? (
                <div className="glass rounded-2xl p-12 text-center">
                  <p className="text-4xl mb-4">🎪</p>
                  <p className="text-white font-medium mb-2">No events yet</p>
                  <button onClick={()=>setTab(2)} className="text-brand-400 text-sm hover:underline">Create your first event →</button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {events.map(e=>{
                    const sold=parseInt(e.total_sold)||0;
                    const cap=parseInt(e.total_capacity)||1;
                    const pct=Math.round(sold/cap*100);
                    return (
                      <div key={e.id} className="glass rounded-2xl overflow-hidden hover:border-brand-700/50 transition-all">
                        <div className="h-32 relative overflow-hidden">
                          {e.banner_image
                            ? <img src={e.banner_image} alt="" className="w-full h-full object-cover"/>
                            : <div className="w-full h-full" style={{background:"linear-gradient(135deg,#1a0030,#0d0020)"}}/>
                          }
                          <div className="absolute inset-0" style={{background:"linear-gradient(to top,rgba(0,0,0,0.7),transparent)"}}/>
                          <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full ${e.status==="on_sale"?"bg-green-900/80 text-green-300":"bg-gray-800 text-gray-400"}`}>{e.status}</span>
                        </div>
                        <div className="p-4">
                          <p className="text-white font-semibold text-sm mb-1">{e.title}</p>
                          <p className="text-gray-500 text-xs mb-3">📅 {e.event_date} · 📍 {e.venue_name||e.city}</p>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">{sold}/{cap} tickets</span>
                            <span className="text-white">{pct}%</span>
                          </div>
                          <div className="h-1.5 bg-white/10 rounded-full mb-3">
                            <div className="h-full rounded-full bg-brand-500" style={{width:`${pct}%`}}/>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={()=>{setActiveEvent(e);setTab(0);}} className="flex-1 text-xs glass py-1.5 rounded-lg text-gray-300 hover:text-white transition">Stats</button>
                            <button onClick={()=>{setActiveEvent(e);setTab(3);}} className="flex-1 text-xs glass py-1.5 rounded-lg text-gray-300 hover:text-white transition">Attendees</button>
                            <button onClick={()=>{setEditingEvent(e);ticketsApi.getEventStats(e.id).then(d=>{ setEditForm({title:e.title,subtitle:e.subtitle||"",venue_name:e.venue_name||"",city:e.city||"",event_date:e.event_date?.slice(0,10)||"",start_time:e.start_time?.slice(0,5)||"",end_time:e.end_time?.slice(0,5)||"",description:e.description||"",status:e.status||"on_sale",banner_image:e.banner_image||"",secret_location:e.secret_location||"",tiers:d.tiers||[]}); }).catch(()=>{ setEditForm({title:e.title,subtitle:e.subtitle||"",venue_name:e.venue_name||"",city:e.city||"",event_date:e.event_date?.slice(0,10)||"",start_time:e.start_time?.slice(0,5)||"",end_time:e.end_time?.slice(0,5)||"",description:e.description||"",status:e.status||"on_sale",banner_image:e.banner_image||"",secret_location:e.secret_location||"",tiers:[]}); });}}
                              className="flex-1 text-xs py-1.5 rounded-lg text-white transition" style={{background:"rgba(155,63,232,0.3)"}}>Edit</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── EDIT EVENT MODAL ── */}
          {editingEvent && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.85)"}}>
              <div className="w-full max-w-2xl rounded-3xl p-6 max-h-[90vh] overflow-y-auto" style={{background:"#0d0020", border:"1px solid rgba(155,63,232,0.3)"}}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display text-xl font-bold text-white">Edit Event</h2>
                  <button onClick={()=>setEditingEvent(null)} className="text-gray-500 hover:text-white text-2xl">×</button>
                </div>
                <div className="space-y-4">
                  {[
                    {label:"Event Title", key:"title", type:"text"},
                    {label:"Subtitle", key:"subtitle", type:"text"},
                    {label:"Banner Image URL", key:"banner_image", type:"text"},
                    {label:"🔒 Secret Location (shown only on ticket)", key:"secret_location", type:"text"},
                    {label:"Venue Name", key:"venue_name", type:"text"},
                    {label:"City", key:"city", type:"text"},
                    {label:"Event Date", key:"event_date", type:"date"},
                    {label:"Start Time", key:"start_time", type:"time"},
                    {label:"End Time", key:"end_time", type:"time"},
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-xs text-gray-400 mb-1 block">{f.label}</label>
                      <input type={f.type} value={editForm[f.key]||""}
                        onChange={e=>setEditForm(p=>({...p,[f.key]:e.target.value}))}
                        className="w-full px-4 py-3 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                        style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)"}}/>
                    </div>
                  ))}
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Description</label>
                    <textarea rows={3} value={editForm.description||""}
                      onChange={e=>setEditForm(p=>({...p,description:e.target.value}))}
                      className="w-full px-4 py-3 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                      style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)"}}/>
                  </div>
                  {/* Tier editing */}
                  {editForm.tiers?.length > 0 && (
                    <div>
                      <label className="text-xs text-gray-400 mb-3 block font-semibold uppercase tracking-wider">🎫 Ticket Tiers</label>
                      <div className="space-y-3">
                        {editForm.tiers.map((t,i) => (
                          <div key={t.id} className="p-4 rounded-xl" style={{background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)"}}>
                            <p className="text-white text-sm font-medium mb-3">{t.name}</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Price (LKR)</label>
                                <input type="number" value={t.price||""}
                                  onChange={e=>{const ts=[...editForm.tiers];ts[i]={...ts[i],price:e.target.value};setEditForm(p=>({...p,tiers:ts}));}}
                                  className="w-full px-3 py-2 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                                  style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)"}}/>
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Total Capacity</label>
                                <input type="number" value={t.capacity||""}
                                  onChange={e=>{const ts=[...editForm.tiers];ts[i]={...ts[i],capacity:e.target.value};setEditForm(p=>({...p,tiers:ts}));}}
                                  className="w-full px-3 py-2 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                                  style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)"}}/>
                              </div>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">{t.sold_count} sold · {t.capacity - t.sold_count} remaining</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Status</label>
                    <select value={editForm.status||"on_sale"}
                      onChange={e=>setEditForm(p=>({...p,status:e.target.value}))}
                      className="w-full px-4 py-3 rounded-xl text-white text-sm focus:outline-none"
                      style={{background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)"}}>
                      <option value="on_sale">On Sale</option>
                      <option value="draft">Draft</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={()=>setEditingEvent(null)}
                    className="flex-1 py-3 rounded-xl text-gray-300 glass transition">Cancel</button>
                  <button disabled={editSaving} onClick={async()=>{
                    setEditSaving(true);
                    try {
                      await ticketsApi.updateEvent(editingEvent.id, {...editForm, tiers: editForm.tiers?.map(t=>({id:t.id, name:t.name, price:parseFloat(t.price), capacity:parseInt(t.capacity)}))});
                      setEvents(prev=>prev.map(e=>e.id===editingEvent.id?{...e,...editForm}:e));
                      setEditingEvent(null);
                      alert("Event updated!");
                    } catch(err) { alert("Error: "+err.message); }
                    finally { setEditSaving(false); }
                  }}
                    className="flex-1 py-3 rounded-xl text-white font-semibold transition disabled:opacity-50"
                    style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                    {editSaving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── CREATE EVENT ── */}
          {tab===2 && <CreateEventForm onCreated={(e)=>{setEvents(prev=>[e,...prev]);setActiveEvent(e);setTab(1);}} />}

          {/* ── ATTENDEES ── */}
          {tab===3 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-display text-2xl font-bold text-white">Attendees</h1>
                <div className="flex gap-2">
                  <input type="text" placeholder="Search name, email, order..." value={search}
                    onChange={e=>setSearch(e.target.value)}
                    className="text-xs px-3 py-2 rounded-lg text-gray-300 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    style={inpStyle}/>
                </div>
              </div>
              <div className="glass rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-gray-400 text-sm">{attendees.length} attendees for <span className="text-white">{activeEvent?.title}</span></p>
                  <button onClick={()=>{
                    const csv = "Name,Email,WhatsApp,Tier,Order,Checked In\n" +
                      attendees.map(a=>`${a.attendee_name},${a.attendee_email},${a.attendee_whatsapp},${a.tier_name},${a.order_ref},${a.checked_in?"Yes":"No"}`).join("\n");
                    const blob = new Blob([csv], {type:"text/csv"});
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href=url; a.download=`attendees-${activeEvent?.slug||"event"}.csv`; a.click();
                  }} className="text-xs glass px-3 py-1.5 rounded-lg text-gray-300 hover:text-white transition">Export CSV</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 text-xs border-b border-white/10">
                        <th className="pb-3">Name</th>
                        <th className="pb-3 hidden md:table-cell">Email</th>
                        <th className="pb-3 hidden md:table-cell">WhatsApp</th>
                        <th className="pb-3">Tier</th>
                        <th className="pb-3">Order</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {attendees.map(a=>(
                        <tr key={a.id} className="hover:bg-white/2 transition group">
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-brand-800 flex items-center justify-center text-xs font-bold text-brand-200 flex-shrink-0">{a.attendee_name[0]}</div>
                              <span className="text-white text-xs font-medium">{a.attendee_name}</span>
                            </div>
                          </td>
                          <td className="py-3 text-gray-400 text-xs hidden md:table-cell">{a.attendee_email}</td>
                          <td className="py-3 text-gray-400 text-xs hidden md:table-cell">{a.attendee_whatsapp}</td>
                          <td className="py-3 text-xs text-brand-300">{a.tier_name}</td>
                          <td className="py-3 text-xs font-mono text-gray-500">{a.order_ref}</td>
                          <td className="py-3">
                            {a.checked_in
                              ? <span className="text-xs bg-green-900/50 text-green-300 px-2 py-0.5 rounded-full">✓ {a.checked_in_at?.slice(11,16)}</span>
                              : <span className="text-xs bg-white/5 text-gray-400 px-2 py-0.5 rounded-full">Pending</span>}
                          </td>
                          <td className="py-3">
                            <button onClick={()=>ticketsApi.resendTicket(a.ticket_uid).then(()=>alert("QR resent!"))}
                              className="text-xs text-brand-400 hover:text-brand-300 opacity-0 group-hover:opacity-100 transition">
                              Resend QR
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!attendees.length && <p className="text-gray-500 text-sm text-center py-8">No attendees yet</p>}
                </div>
              </div>
            </div>
          )}

          {/* ── QR SCANNER ── */}
          {tab===4 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-display text-2xl font-bold text-white">QR Scanner</h1>
                <div className="flex gap-2">
                  <button onClick={()=>setScannerMode("ticket")}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${scannerMode==="ticket"?"text-white":"glass text-gray-400 hover:text-white"}`}
                    style={scannerMode==="ticket"?{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}:{}}>
                    🎫 Ticket Scan
                  </button>
                  <button onClick={()=>setScannerMode("wristband")}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${scannerMode==="wristband"?"text-white":"glass text-gray-400 hover:text-white"}`}
                    style={scannerMode==="wristband"?{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}:{}}>
                    🎀 Wristband Lookup
                  </button>
                </div>
              </div>
              {scannerMode === "wristband" && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="glass rounded-2xl p-6">
                    <h2 className="font-semibold text-white mb-1">Lookup by Wristband</h2>
                    <p className="text-gray-500 text-sm mb-4">Enter or scan a wristband number to see who it belongs to</p>
                    <div className="flex gap-2 mb-4">
                      <input type="text" placeholder="Wristband number e.g. 1279"
                        value={wristbandLookup} onChange={e=>setWristbandLookup(e.target.value)}
                        onKeyDown={e=>e.key==="Enter"&&lookupWristband()}
                        className="flex-1 px-4 py-3 rounded-xl text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 text-center tracking-widest"
                        style={{background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)"}}/>
                      <button onClick={lookupWristband} disabled={wristbandLookupLoading || !wristbandLookup.trim()}
                        className="px-4 py-3 rounded-xl text-white font-bold text-sm transition disabled:opacity-50"
                        style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                        {wristbandLookupLoading ? "..." : "🔍 Find"}
                      </button>
                    </div>
                    {wristbandLookupResult && (
                      <div className={`rounded-2xl p-5 border-2 ${wristbandLookupResult.success?"border-green-500/40 bg-green-900/10":"border-red-500/40 bg-red-900/10"}`}>
                        {wristbandLookupResult.success ? (
                          <div>
                            <div className="flex items-center gap-3 mb-4">
                              <span className="text-4xl">🎀</span>
                              <div>
                                <p className="text-green-300 font-black text-xl">Wristband #{wristbandLookupResult.ticket?.wristband_number}</p>
                                <p className="text-gray-400 text-sm">Valid & Assigned</p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {[
                                ["👤 Attendee",  wristbandLookupResult.ticket?.attendee_name],
                                ["🎫 Tier",      wristbandLookupResult.ticket?.tier_name],
                                ["🎪 Event",     wristbandLookupResult.ticket?.event_title],
                                ["📋 Order",     wristbandLookupResult.ticket?.order_ref],
                                ["⏰ Assigned",  wristbandLookupResult.ticket?.wristband_assigned_at ? new Date(wristbandLookupResult.ticket.wristband_assigned_at).toLocaleTimeString() : ""],
                              ].filter(([,v])=>v).map(([l,v]) => (
                                <div key={l} className="flex justify-between text-sm">
                                  <span className="text-gray-400">{l}</span>
                                  <span className="text-white font-semibold">{v}</span>
                                </div>
                              ))}
                            </div>
                            <button onClick={()=>{setWristbandLookup("");setWristbandLookupResult(null);}}
                              className="mt-4 w-full text-xs text-gray-500 hover:text-white transition py-1">
                              Look up another →
                            </button>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-4xl mb-2">❓</p>
                            <p className="text-red-300 font-bold">Wristband Not Found</p>
                            <p className="text-gray-500 text-sm mt-1">{wristbandLookupResult.error}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Stats panel */}
                  <div className="glass rounded-2xl p-5">
                    <h2 className="font-semibold text-white mb-4">Wristband Stats</h2>
                    <p className="text-gray-500 text-sm">Switch to Ticket Scan mode to see check-in stats</p>
                  </div>
                </div>
              )}

              {scannerMode === "ticket" && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="glass rounded-2xl p-6">
                  <QRScanner
                    onScan={async (uid) => {
                      if (!activeEvent) return;
                      setScanInput(uid);
                      try {
                        const r = await ticketsApi.processCheckin(uid, activeEvent.id);
                        setScanResult(r);
                        if (r.result === "success") ticketsApi.getCheckins(activeEvent.id).then(d => setCheckins(d.checkins||[]));
                      } catch(e) { setScanResult({result:"invalid", message:e.message}); }
                    }}
                    onError={(msg) => setScanResult({result:"invalid", message:msg})}
                  />
                  <p className="text-xs text-gray-500 text-center mb-3">— or enter manually —</p>
                  <div className="flex gap-2">
                    <input type="text" placeholder="Ticket UID or order ref..."
                      value={scanInput} onChange={e=>setScanInput(e.target.value)}
                      onKeyDown={e=>e.key==="Enter"&&doScan()}
                      className="flex-1 px-3 py-2.5 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      style={inpStyle}/>
                    <button onClick={doScan} className="px-4 py-2.5 bg-brand-700 hover:bg-brand-600 rounded-xl text-white text-sm font-medium transition">Verify</button>
                  </div>

                  {scanResult && (
                    <div className={`mt-4 rounded-2xl p-5 border-2 ${
                      scanResult.result==="success"?"bg-green-900/20 border-green-500/50":
                      scanResult.result==="duplicate"?"bg-yellow-900/20 border-yellow-500/50":
                      "bg-red-900/20 border-red-500/50"}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl">{scanResult.result==="success"?"✅":scanResult.result==="duplicate"?"⚠️":"❌"}</span>
                        <div>
                          <p className={`font-black text-xl ${scanResult.result==="success"?"text-green-300":scanResult.result==="duplicate"?"text-yellow-300":"text-red-300"}`}>
                            {scanResult.result==="success"?"Check-in Success!":scanResult.result==="duplicate"?"Already Checked In!":"Invalid Ticket"}
                          </p>
                          <p className="text-gray-400 text-sm">{scanResult.message}</p>
                        </div>
                      </div>
                      {scanResult.ticket && (
                        <div className="space-y-2 mt-3 pt-3 border-t border-white/10">
                          {[
                            {l:"👤 Attendee", v:scanResult.ticket.attendeeName},
                            {l:"🎫 Tier",     v:scanResult.ticket.tier},
                            {l:"📋 Order",    v:scanResult.ticket.orderRef},
                          ].filter(r=>r.v).map(r=>(
                            <div key={r.l} className="flex justify-between text-sm">
                              <span className="text-gray-400">{r.l}</span>
                              <span className="text-white font-semibold">{r.v}</span>
                            </div>
                          ))}
                          {scanResult.result==="success" && (
                            <div className="mt-2 p-2 rounded-lg text-center text-xs text-green-400 font-semibold"
                              style={{background:"rgba(34,197,94,0.1)"}}>
                              🕐 Checked in at {new Date().toLocaleTimeString()}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── WRISTBAND ASSIGNMENT ── */}
                      {(scanResult.result==="success" || scanResult.result==="duplicate") && scanResult.ticket && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <p className="text-xs font-bold text-gray-400 mb-2 uppercase tracking-wider">🎀 Assign Wristband</p>
                          {wristbandResult?.success ? (
                            <div className="p-3 rounded-xl text-center" style={{background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.3)"}}>
                              <p className="text-green-300 font-black text-lg">🎀 #{wristbandResult.wristband_number}</p>
                              <p className="text-green-400 text-xs mt-0.5">Wristband assigned!</p>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <input type="text"
                                placeholder="Wristband number e.g. 1279"
                                value={wristbandInput}
                                onChange={e => { setWristbandInput(e.target.value); setWristbandResult(null); }}
                                onKeyDown={e => e.key==="Enter" && assignWristband()}
                                className="flex-1 px-3 py-2.5 rounded-xl text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 text-center tracking-widest"
                                style={{background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)"}}/>
                              <button onClick={assignWristband} disabled={wristbandLoading || !wristbandInput.trim()}
                                className="px-4 py-2.5 rounded-xl text-white font-bold text-sm transition disabled:opacity-50"
                                style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                                {wristbandLoading ? "..." : "Assign"}
                              </button>
                            </div>
                          )}
                          {wristbandResult && !wristbandResult.success && (
                            <p className="text-red-400 text-xs mt-1.5">{wristbandResult.error}</p>
                          )}
                        </div>
                      )}

                      <button onClick={()=>{ setScanResult(null); setWristbandResult(null); setWristbandInput(""); }}
                        className="mt-3 w-full text-xs text-gray-500 hover:text-white transition py-1">
                        Scan next ticket →
                      </button>
                    </div>
                  )}
                </div>

                <div className="glass rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-white">Recent Check-ins</h2>
                    <span className="text-xs text-gray-500">{checkins.length} today</span>
                  </div>
                  {/* Stats bar */}
                  {checkins.length > 0 && (
                    <div className="mb-4 p-3 rounded-xl" style={{background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.2)"}}>
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-green-400 font-semibold">{checkins.length} checked in</span>
                        <span className="text-gray-500">{stats?.tiers?.[0]?.capacity - checkins.length} remaining</span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all"
                          style={{width:`${Math.min(100,Math.round(checkins.length/(stats?.tiers?.[0]?.capacity||1)*100))}%`}}/>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {checkins.map((c,i)=>(
                      <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl transition hover:bg-green-900/10"
                        style={{background:"rgba(34,197,94,0.05)", border:"1px solid rgba(34,197,94,0.1)"}}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-green-300 flex-shrink-0"
                          style={{background:"rgba(34,197,94,0.15)"}}>
                          {checkins.length - i}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold">{c.attendee_name}</p>
                          <p className="text-xs text-gray-500">{c.tier_name} · {c.order_ref}</p>
                        </div>
                        <span className="text-xs text-green-400 flex-shrink-0 font-mono">{c.scanned_at?.slice(11,16)}</span>
                      </div>
                    ))}
                    {!checkins.length && (
                      <div className="text-center py-10">
                        <p className="text-3xl mb-2">🎫</p>
                        <p className="text-gray-500 text-sm">No check-ins yet</p>
                        <p className="text-gray-600 text-xs mt-1">Scan tickets to see them here</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              )}
            </div>
          )}

          {/* ── WRISTBANDS ── */}
          {tab===5 && <WristbandScanner events={events} activeEvent={activeEvent} setActiveEvent={setActiveEvent}/>}

          {/* ── PAYOUTS ── */}
          {tab===6 && <PayoutsTab />}

        </div>
      </div>
    </div>
  );
}

// ── CREATE EVENT FORM ─────────────────────────────────────────────
function CreateEventForm({ onCreated }) {
  const [form, setForm] = useState({
    title:"", subtitle:"", description:"", category:"Music",
    event_date:"", start_time:"", end_time:"", venue_name:"",
    venue_address:"", city:"Colombo", banner_image:"", thumb_image:"", tags:[]
  });
  const [tiers, setTiers] = useState([
    { name:"General Admission", price:"", capacity:"", color:"blue", perks:["General access"] }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const categories = ["Music","Comedy","Tech","Wellness","Sports","Art","Food","Other"];
  const colors = ["blue","gold","purple","teal","red"];

  const updateTier = (i, field, val) => {
    const t = [...tiers]; t[i] = {...t[i], [field]: val}; setTiers(t);
  };
  const addTier = () => setTiers([...tiers, {name:"", price:"", capacity:"", color:"blue", perks:[]}]);
  const removeTier = (i) => setTiers(tiers.filter((_,idx)=>idx!==i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.event_date || !form.start_time || !form.venue_name)
      return setError("Title, date, time and venue are required");
    if (tiers.some(t => !t.name || !t.price || !t.capacity))
      return setError("All tier fields are required");
    setLoading(true); setError("");
    try {
      const d = await ticketsApi.createEvent({
        ...form,
        tags: form.tags,
        tiers: tiers.map(t => ({ ...t, price: parseFloat(t.price), capacity: parseInt(t.capacity), perks: t.perks }))
      });
      setSuccess("Event created! Pending admin approval to go on sale.");
      setTimeout(() => onCreated({ id: d.eventId, title: form.title, slug: d.slug, status: "draft", ...form }), 1500);
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const inpCls = "w-full px-3 py-2.5 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500";
  const inpStyle = { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)" };
  const label = (text) => <label className="text-xs text-gray-400 mb-1 block">{text}</label>;

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-white mb-6">Create New Event</h1>
      {error && <div className="bg-red-900/30 border border-red-700/40 rounded-xl p-3 mb-4 text-red-300 text-sm">{error}</div>}
      {success && <div className="bg-green-900/30 border border-green-700/40 rounded-xl p-3 mb-4 text-green-300 text-sm">{success}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="glass rounded-2xl p-5">
          <h2 className="font-semibold text-white mb-4">Event Details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              {label("Event Title *")}
              <input type="text" placeholder="e.g. Neon Nights 2026" value={form.title}
                onChange={e=>setForm({...form,title:e.target.value})} className={inpCls} style={inpStyle} required/>
            </div>
            <div className="sm:col-span-2">
              {label("Subtitle")}
              <input type="text" placeholder="e.g. Sri Lanka's Biggest Music Festival" value={form.subtitle}
                onChange={e=>setForm({...form,subtitle:e.target.value})} className={inpCls} style={inpStyle}/>
            </div>
            <div>
              {label("Category *")}
              <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})}
                className={inpCls} style={inpStyle}>
                {categories.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              {label("City *")}
              <input type="text" placeholder="Colombo" value={form.city}
                onChange={e=>setForm({...form,city:e.target.value})} className={inpCls} style={inpStyle}/>
            </div>
            <div>
              {label("Event Date *")}
              <input type="date" value={form.event_date}
                onChange={e=>setForm({...form,event_date:e.target.value})} className={inpCls} style={inpStyle} required/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                {label("Start Time *")}
                <input type="time" value={form.start_time}
                  onChange={e=>setForm({...form,start_time:e.target.value})} className={inpCls} style={inpStyle} required/>
              </div>
              <div>
                {label("End Time")}
                <input type="time" value={form.end_time}
                  onChange={e=>setForm({...form,end_time:e.target.value})} className={inpCls} style={inpStyle}/>
              </div>
            </div>
            <div className="sm:col-span-2">
              {label("Venue Name *")}
              <input type="text" placeholder="e.g. Galle Face Green" value={form.venue_name}
                onChange={e=>setForm({...form,venue_name:e.target.value})} className={inpCls} style={inpStyle} required/>
            </div>
            <div className="sm:col-span-2">
              {label("Banner Image URL")}
              <input type="url" placeholder="https://..." value={form.banner_image}
                onChange={e=>setForm({...form,banner_image:e.target.value,thumb_image:e.target.value})} className={inpCls} style={inpStyle}/>
            </div>
            <div className="sm:col-span-2">
              {label("Description")}
              <textarea rows={4} placeholder="Tell attendees what to expect..." value={form.description}
                onChange={e=>setForm({...form,description:e.target.value})}
                className={inpCls} style={{...inpStyle, resize:"vertical"}}/>
            </div>
          </div>
        </div>

        {/* Ticket Tiers */}
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white">Ticket Tiers</h2>
            <button type="button" onClick={addTier}
              className="text-xs bg-brand-800/60 hover:bg-brand-700 text-brand-300 px-3 py-1.5 rounded-lg transition">
              + Add Tier
            </button>
          </div>
          <div className="space-y-4">
            {tiers.map((t,i)=>(
              <div key={i} className="border border-white/10 rounded-xl p-4" style={{background:"rgba(255,255,255,0.02)"}}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white text-sm font-medium">Tier {i+1}</span>
                  {tiers.length > 1 && (
                    <button type="button" onClick={()=>removeTier(i)} className="text-xs text-red-400 hover:text-red-300">Remove</button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    {label("Tier Name *")}
                    <input type="text" placeholder="e.g. VIP" value={t.name}
                      onChange={e=>updateTier(i,"name",e.target.value)} className={inpCls} style={inpStyle}/>
                  </div>
                  <div>
                    {label("Color")}
                    <select value={t.color} onChange={e=>updateTier(i,"color",e.target.value)}
                      className={inpCls} style={inpStyle}>
                      {colors.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    {label("Price (LKR) *")}
                    <input type="number" placeholder="2500" value={t.price}
                      onChange={e=>updateTier(i,"price",e.target.value)} className={inpCls} style={inpStyle}/>
                  </div>
                  <div>
                    {label("Capacity *")}
                    <input type="number" placeholder="500" value={t.capacity}
                      onChange={e=>updateTier(i,"capacity",e.target.value)} className={inpCls} style={inpStyle}/>
                  </div>
                  <div className="sm:col-span-2">
                    {label("Perks (one per line)")}
                    <textarea rows={3} placeholder={"General access\nFestival wristband\nPriority entry"}
                      value={t.perks.join("\n")}
                      onChange={e=>updateTier(i,"perks",e.target.value.split("\n"))}
                      className={inpCls} style={{...inpStyle, resize:"vertical"}}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button type="submit" disabled={loading}
          className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:scale-[1.01] disabled:opacity-60"
          style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
          {loading ? "Creating Event..." : "Create Event →"}
        </button>
      </form>
    </div>
  );
}

// ── PAYOUTS TAB ───────────────────────────────────────────────────
function PayoutsTab() {
  const [form, setForm] = useState({ bank_name:"", account_number:"", account_name:"" });
  const [submitted, setSubmitted] = useState(false);
  const inpCls = "w-full px-3 py-2.5 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500";
  const inpStyle = { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch("/api/dropnail/wallet/payout", {
        method: "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${localStorage.getItem("organiser_token")}` },
        body: JSON.stringify(form)
      });
      setSubmitted(true);
    } catch {}
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-white mb-6">Payouts</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <h2 className="font-semibold text-white mb-4">Request Payout</h2>
          {submitted ? (
            <div className="text-center py-8">
              <p className="text-4xl mb-3">✅</p>
              <p className="text-green-300 font-medium">Payout request submitted!</p>
              <p className="text-gray-500 text-sm mt-1">Our team will process within 3-5 business days.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {[
                {l:"Bank Name", f:"bank_name", p:"e.g. Commercial Bank"},
                {l:"Account Number", f:"account_number", p:"000000000000"},
                {l:"Account Holder Name", f:"account_name", p:"Full name on account"},
              ].map(x=>(
                <div key={x.f}>
                  <label className="text-xs text-gray-400 mb-1 block">{x.l}</label>
                  <input type="text" placeholder={x.p} value={form[x.f]}
                    onChange={e=>setForm({...form,[x.f]:e.target.value})} required
                    className={inpCls} style={inpStyle}/>
                </div>
              ))}
              <button type="submit"
                className="w-full py-3 rounded-xl font-semibold text-white transition"
                style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                Request Payout
              </button>
            </form>
          )}
        </div>
        <div className="glass rounded-2xl p-6">
          <h2 className="font-semibold text-white mb-4">Payout Info</h2>
          <div className="space-y-3 text-sm">
            {[
              {i:"💰", t:"Minimum payout", d:"LKR 5,000"},
              {i:"⏱️", t:"Processing time", d:"3-5 business days"},
              {i:"📊", t:"Platform fee", d:"No extra fees charged to customers"},
              {i:"🏦", t:"Payment method", d:"Bank transfer (LKR)"},
            ].map(x=>(
              <div key={x.t} className="flex items-start gap-3 p-3 rounded-xl" style={{background:"rgba(255,255,255,0.04)"}}>
                <span className="text-xl">{x.i}</span>
                <div><p className="text-white font-medium">{x.t}</p><p className="text-gray-500 text-xs">{x.d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}